<template>
    <div class="container-account-imports app-body"
         id="view">
        <div class="container-listing min-height-screen">
            <div class="container-header">
                <div class="d-flex">
                    <h3 class="listing-heading">Lists</h3>

                    <el-dropdown
                        size="medium"
                        trigger="click"
                        class="ml-auto"
                        v-popover:import-contacts-popover
                        ref="importSelection"
                        data-testid="create-list-dropdown"
                        @command="importContact">
                        <el-button
                            class="import-selection-button"
                            type="primary"
                            data-testid="create-list-button">
                            Create List <i class="el-icon-arrow-down el-icon--right ml-2"></i>
                        </el-button>
                        <template v-slot:dropdown>
                            <el-dropdown-menu class="account-import-selection-container">
                                <el-dropdown-item
                                    command="import_from_hubspot"
                                    class="d-flex align-items-center"
                                    data-testid="import-from-hubspot-option"
                                    v-if="current_company?.hubspot_integration_enabled">
                                    <div class="icon-container hubspot-icon-container">
                                        <i class="fab fa-hubspot item-icon"></i>
                                    </div>
                                    <span class="ml-2">Import from HubSpot</span>
                                </el-dropdown-item>
                                <el-dropdown-item
                                    command="import_from_salesforce"
                                    class="d-flex align-items-center"
                                    data-testid="import-from-salesforce-option"
                                    v-if="current_company?.salesforce_integration_enabled">
                                    <div class="icon-container salesforce-icon-container">
                                        <i class="fab fa-salesforce"></i>
                                    </div>
                                    <span class="ml-2">Import from Salesforce</span>
                                </el-dropdown-item>
                                <el-dropdown-item
                                    command="import_from_csv"
                                    class="d-flex align-items-center"
                                    data-testid="import-from-csv-option">
                                    <img
                                        src="/assets/images/icons/csv-svg-icon-12.svg"
                                        alt=""
                                        class="csv-icon">
                                    <span class="ml-2">Import CSV</span>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>

                    <video-modal class="pl-2"
                                 title="🚀 Mastering List Management in Aloware"
                                 cookie-name="lists"
                                 notes="🔥 Unleash the power of <strong>Aloware Lists!</strong> </br></br> 📊 Organize, categorize, and skyrocket your contact management game. </br></br> Dive in and watch your efficiency soar! 💥📈"
                                 video-url="https://www.youtube.com/embed/aSdCAyV0bW4?si=EGKB-l66moF9q9HZ"
                                 learn-more-link="https://support.aloware.com/en/articles/9031374-user-s-guide-simplifying-contact-imports-for-admins-and-agents"
                                 activator-size="small"
                                 v-if="isTrial"/>
                </div>

              <p class="mb-1 text-grey-8 _600">
                    Create public lists that agents can access in {{ name }} Talk.
                    <a
                        class="text-blue _600"
                        href="https://support.aloware.com/en/articles/9034179-creating-contact-lists-for-users-in-aloware-talk"
                        target="_blank"
                        data-testid="learn-more-link"
                        v-if="!isTrial">
                        Learn more
                    </a>
                </p>
            </div>

            <div class="container-body">
                <div class="container-listing-tools d-flex flex-wrap mb-1">
                    <div class="d-flex flex-wrap mr-auto">
                        <div class="d-flex mr-2 search-toolbar">
                            <el-input
                                id="search-input"
                                ref="contactListSearch"
                                placeholder="Search contact lists..."
                                v-model="filters.search"
                                clearable
                                data-testid="search-contact-lists-input"
                                @keyup.native.prevent="searchContactLists"
                                @change="searchContactLists">
                                <i slot="prefix" class="el-input__icon el-icon-search"></i>
                            </el-input>
                        </div>
                    </div>
                    <div class="d-flex mt-2 mt-xl-0 flex-wrap flex-lg-nowrap">
                        <div class="mr-2 align-self-center">
                            <span class="text-grey">Total Contacts List:</span> <strong>{{ pagination.total }}</strong>
                        </div>

                        <el-pagination
                            class="align-self-center"
                            layout="prev, pager, next"
                            :current-page.sync="filters.page"
                            :page-size.sync="filters.size"
                            :total="pagination.total"
                            :pager-count="5"
                            data-testid="lists-pagination-top"
                            @size-change="fetchContactList(1)"
                            @current-change="fetchContactList">
                        </el-pagination>
                    </div>
                </div>

                <div class="table-responsive">
                    <el-table class="w-full"
                              fit
                              stripe
                              data-testid="lists-table"
                              :data="pagination.data"
                              :default-sort="{prop: 'created_at', order: 'descending'}"
                              v-loading="loading"
                              @sort-change="onSortChange">
                        <el-table-column label="ID"
                                         prop="id"
                                         width="120"
                                         sortable="custom"
                                         data-testid="column-id">
                            <template v-slot="scope">
                                {{ scope.row.id }}
                            </template>
                        </el-table-column>

                        <el-table-column label="Name"
                                         prop="name"
                                         sortable="custom"
                                         :min-width="260"
                                         data-testid="column-name">
                            <template v-slot="scope">
                                <el-button type="text"
                                           class="p-0 text-blue"
                                           :data-testid="`open-list-button-${scope.row.id}`"
                                           v-if="scope.row.show_in_public_folder"
                                           @click="openAdminList(scope.row)">
                                    <i class="material-icons text-blue">launch</i> {{ scope.row.name }}
                                </el-button>
                                <span v-else class="_400">
                                    {{ scope.row.name }}
                                </span>
                            </template>
                        </el-table-column>

                        <el-table-column label="Owner Name"
                                         prop="owner_name"
                                         sortable="custom"
                                         :min-width="180"
                                         data-testid="column-owner-name">
                            <template v-slot="scope">
                                {{ scope.row.owner_name }}
                            </template>
                        </el-table-column>

                        <el-table-column label="Date Created"
                                         prop="created_at"
                                         width="225"
                                         sortable="custom"
                                         data-testid="column-date-created">
                            <template v-slot="scope">
                                {{ scope.row.created_at | fixDateTime }}
                            </template>
                        </el-table-column>

                        <el-table-column label="Type"
                                         prop="type"
                                         width="170"
                                         sortable="custom"
                                         data-testid="column-type">
                            <template v-slot="scope">
                                {{ getContactListType(scope.row) }}
                            </template>
                        </el-table-column>

                        <el-table-column label="Show in Public Folder"
                                         prop="show_in_public_folder"
                                         width="210"
                                         sortable="custom"
                                         data-testid="column-show-in-public-folder">
                            <template v-slot="scope">
                                <el-switch active-color="#13ce66"
                                           :value="scope.row.show_in_public_folder"
                                           :data-testid="`show-public-folder-switch-${scope.row.id}`"
                                           @change="handleShowInPublicFolder(scope.row)">
                                </el-switch>
                            </template>
                        </el-table-column>

                        <el-table-column label="Source"
                                         prop="source_name"
                                         width="150"
                                         sortable="custom"
                                         data-testid="column-source">
                            <template v-slot="scope">
                                {{ scope.row.source_name | ucwords }}
                            </template>
                        </el-table-column>

                        <el-table-column label="Import Status"
                                         prop="import_status_name"
                                         width="150"
                                         sortable="custom"
                                         data-testid="column-import-status">
                            <template v-slot="scope">
                                <span :class="`import-status-${scope.row.import_status + 1}`">
                                    {{ scope.row.import_status_name | ucwords }}
                                </span>
                            </template>
                        </el-table-column>

                        <el-table-column label="Imported At"
                                         prop="imported_at"
                                         width="240"
                                         sortable="custom"
                                         data-testid="column-imported-at">
                            <template v-slot="scope">
                                {{ scope.row.imported_at | fixDateTime }}
                            </template>
                        </el-table-column>

                        <el-table-column label="Operations"
                                         width="190"
                                         fixed="right"
                                         data-testid="column-operations">
                            <template v-slot="scope">
                                <button class="btn btn-block btn-info btn-sm"
                                        :data-testid="`view-logs-button-${scope.row.id}`"
                                        v-if="scope.row.source === 3"
                                        @click="showPullHubspotListLogModal(scope.row)">
                                    <i class="fas fa-info-circle pull-left"></i>
                                    View Logs
                                </button>
                                <button class="btn btn-block btn-dark-warning text-white btn-sm"
                                        :data-testid="`split-button-${scope.row.id}`"
                                        :disabled="isCheckingListCount"
                                        v-if="shouldShowSplitOption(scope.row)"
                                        @click="showSplitListForm(scope.row)">
                                    <i :class="isCheckingListCount ? 'fas fa-spinner fa-spin' : 'fas fa-clone'" class="pull-left"></i>
                                    Split
                                </button>
                                <button class="btn btn-block btn-warning btn-sm"
                                        :data-testid="`assign-contacts-button-${scope.row.id}`"
                                        @click="showAssignContactForm(scope.row)">
                                    <i class="fas fa-sign-in-alt pull-left"></i>
                                    Assign Contacts
                                </button>
                                <div class="my-2"
                                     v-if="current_company?.automation_enabled">
                                    <workflow-bulk-enroller model="contact_list"
                                                            :id="scope.row.id"
                                                            :data-testid="`enroll-contacts-button-${scope.row.id}`">
                                        Enroll Contacts
                                    </workflow-bulk-enroller>
                                </div>
                                <add-list-to-power-dialer :contact-list="scope.row"
                                                          :data-testid="`add-to-power-dialer-${scope.row.id}`"
                                                          v-if="current_company?.auto_dialer_enabled">
                                </add-list-to-power-dialer>
                                <button class="btn btn-block info text-white btn-sm"
                                        :data-testid="`rename-button-${scope.row.id}`"
                                        @click="stageToEditContactList(scope.row)">
                                    <i class="fas fa-pen pull-left"></i>
                                    Rename
                                </button>
                                <button class="btn btn-block btn-danger btn-sm"
                                        :data-testid="`delete-button-${scope.row.id}`"
                                        @click="deleteContactList(scope.row)">
                                    <i class="fa fa-trash pull-left"></i>
                                    Delete
                                </button>
                            </template>
                        </el-table-column>
                    </el-table>

                    <div v-if="!loading && pagination.data.length <= 0"
                         class="text-center mx-auto w-50 py-5">
                        <div class="w-75 mx-auto">
                            <img-empty></img-empty>
                        </div>
                        <p class="lead">
                            We can't seem to find contact list
                            <span v-if="filters.search.length > 0">that matches "{{ filters.search }}"</span>
                        </p>
                    </div>
                </div>
            </div>

            <div class="container-footer d-flex mb-5">
                <div class="ml-auto">
                    <el-pagination
                        layout="sizes, prev, pager, next"
                        :current-page.sync="filters.page"
                        :page-sizes="[10, 20, 50, 100]"
                        :page-size.sync="filters.size"
                        :hide-on-single-page="true"
                        :total="pagination.total"
                        data-testid="lists-pagination-bottom"
                        @size-change="fetchContactList(1)"
                        @current-change="fetchContactList">
                    </el-pagination>
                </div>
            </div>
        </div>

        <el-dialog
            width="35vw"
            top="10vh"
            append-to-body
            title="Rename Contact List"
            data-testid="rename-list-dialog"
            :before-close="closeDialog"
            :visible.sync="show_dialog">
            <el-form
                v-if="selected_model"
                :model="selected_model"
                ref="rename_form">
                <el-form-item
                    label="Name"
                    prop="name"
                    :error="errors.first('name')"
                    :rules="{required: true, message: 'Please specify the name.', trigger: ['blur', 'change']}">
                    <el-input
                        v-model="selected_model.name"
                        size="small"
                        placeholder="Static List One..."
                        data-testid="rename-list-input">
                    </el-input>
                </el-form-item>
            </el-form>

            <div
                slot="footer"
                class="dialog-footer">
                <el-button
                    size="small"
                    class="flex-grow-1"
                    data-testid="rename-list-cancel-button"
                    @click="closeDialog">
                    Cancel
                </el-button>
                <el-button
                    :disabled="!isNameChanged"
                    size="small"
                    type="success"
                    data-testid="rename-list-save-button"
                    @click="updateContactListName">
                    Save
                </el-button>
            </div>
        </el-dialog>

        <import-wizard
            :is_contact_list="true"
            @importStarted="handleImportStarted"
            ref="csv_import">
        </import-wizard>

        <admin-list-import-from-hubspot-list
            :contact_list="selected_model"
            @importStarted="handleImportStarted"
            ref="import_from_hubspot_list">
        </admin-list-import-from-hubspot-list>

        <admin-list-import-from-salesforce-list
            :contact_list="selected_model"
            @importStarted="handleImportStarted"
            ref="import_from_salesforce_list">
        </admin-list-import-from-salesforce-list>

        <admin-list-assign-contact-form
            @closed="closeAssignmentForm"
            :contact_list="selected_model"
            ref="assign_contact_form">
        </admin-list-assign-contact-form>

        <admin-split-list-form ref="split_list_form"
                               :contact_list="selected_model"
                               @closed="closeSplitListForm"
                               @hidden="hideSplitListForm">
        </admin-split-list-form>

        <pull-hubspot-list-logs
            ref="pull_hubspot_list_logs"
            :contactList="selectedContactList">
        </pull-hubspot-list-logs>
    </div>
</template>

<script>
    import debounce from 'lodash/debounce'
    import cloneDeep from 'lodash/cloneDeep'
    import auth from '../../auth'
    import {mapState, mapGetters} from 'vuex'
    import {
        acl_mixin,
        paginator_mixin,
        scroll_mixin,
        validator_mixin,
        kyc_mixin
    } from '../../mixins'
    import ErrorBag from '../../ErrorBag'
    import ImgEmpty from "../../components/misc/img-empty"
    import ImportWizard from "../../components/import-wizard/import-wizard"
    import AdminListAssignContactForm from '../../components/admin-list/admin-list-assign-contact-form'
    import AdminSplitListForm from '../../components/admin-list/admin-split-list-form'
    import AdminListImportFromHubspotList from  '../../components/admin-list/admin-list-import-from-hubspot-list'
    import AdminListImportFromSalesforceList from  '../../components/admin-list/admin-list-import-from-salesforce-list'
    import PullHubspotListLogs from '../../components/pull-hubspot-list-logs'
    import * as ContactListTypes from '../../constants/contact-list-types'
    import VideoModal from '../../components/video-modal.vue'
    import AddListToPowerDialer from '../../components/add-list-to-power-dialer'
    import WorkflowBulkEnroller from '../../components/automations/workflow-bulk-enroller'

    export default {
        name: "AdminListIndex",

        mixins: [
            validator_mixin,
            acl_mixin,
            paginator_mixin,
            scroll_mixin,
            kyc_mixin
        ],

        components: {
            ImgEmpty,
            ImportWizard,
            AdminListAssignContactForm,
            AdminSplitListForm,
            AdminListImportFromHubspotList,
            AdminListImportFromSalesforceList,
            PullHubspotListLogs,
            VideoModal,
            AddListToPowerDialer,
            WorkflowBulkEnroller,
        },

        data() {
            const columns = [
                {label: 'Name', prop: 'name', show_overflow_tooltip: true, sortable: true},
                {label: 'Date Created', prop: 'created_at', show_overflow_tooltip: true, sortable: true},
                {label: 'Type', prop: 'type', show_overflow_tooltip: true, sortable: true},
                {label: 'Show in Public Folder', prop: 'show_in_public_folder', show_overflow_tooltip: false, sortable: false},
                {label: 'Open in Alo Talk', prop: 'open_in_alo_talk', show_overflow_tooltip: false, sortable: false},
            ]
            return {
                auth: auth,
                isCheckingListCount: false,
                pagination: {
                    total: 0,
                    data: []
                },
                filters: {
                    page: 1,
                    size: 10,
                    search: '',
                    sort: 'created_at',
                    order: 'desc'
                },
                loading: false,
                selectedContactList: {},
                show_dialog: false,
                contacts_list: [],
                columns: columns,
                selected_model: {},
                name: '',
                errors: new ErrorBag({}),
                ContactListTypes,
                minimumContactsToSplit: 50
            }
        },

        computed: {
            ...mapState('cache', ['current_company']),
            ...mapGetters('cache', [
                'isTrial',
            ]),

            isNameChanged() {
                let from_list = this.pagination.data.find(d => this.selected_model && d.id === this.selected_model.id)

                return from_list && from_list.name !== this.selected_model.name
            }
        },

        created() {
            this.fetchContactList(1)

            VueEvent.listen('contact_list_import_csv', _ => {
                this.notification('success', 'Contacts successfully imported.')

                this.fetchContactList(1)
            })

            VueEvent.listen('contact_list_import_hubspot', _ => {
                this.notification('success', 'Contacts successfully imported.')

                this.fetchContactList(1)
            })

            VueEvent.listen('contact_list_import_salesforce', _ => {
                this.notification('success', 'Contacts successfully imported.')

                this.fetchContactList(1)
            })

            VueEvent.listen('contact_list_created', _ => {
                this.notification('success', 'Contact list successfully created.')

                this.fetchContactList(1)
            })
        },

        mounted() {
            this.setTitle()

            // focus contact list search input on page visit
            if (this.$refs.contactListSearch) {
                this.$refs.contactListSearch.focus()
            }
        },

        methods: {
            getContactListType(contact_list) {
                switch (contact_list.type) {
                    case ContactListTypes.STATIC_LIST:
                        return 'Static'
                    case ContactListTypes.DYNAMIC_LIST:
                        return 'Dynamic'
                    case ContactListTypes.DYNAMIC_REMOTE_LIST:
                        return 'Integration Dynamic'
                    default:
                        return 'Unknown'
                }
            },

            showPullHubspotListLogModal(contactList) {
                if (this.$refs.pull_hubspot_list_logs) {
                  this.selectedContactList = contactList
                  this.$refs.pull_hubspot_list_logs.show_dialog = true
                }
            },

            setTitle() {
                axios.get('/get-statics')
                    .then(res => {
                        this.name = res.data.name
                        this.setPageTitle('Lists - ' + this.name)
                    })
                    .catch(err => {
                        this.setPageTitle('Lists - Aloware')
                        console.log(err)
                        this.$root.handleErrors(err.response)
                    })
            },

            openAdminList(contact_list) {
                // not public list? then do not open in AloTalk
                if (!contact_list.show_in_public_folder) {
                    return
                }

                let talk_url = this.getTalkUrl()

                if (talk_url) {
                    talk_url += `/contacts/list/${contact_list.id}?type=public`
                    window.open(talk_url)
                }
            },

            showAssignContactForm(contact_list) {
                if (this.$refs.assign_contact_form) {
                    this.selected_model = cloneDeep(contact_list)
                    this.$refs.assign_contact_form.show_dialog = true
                }
            },

            showSplitListForm(list) {
                this.isCheckingListCount = true;
                this.$APIV2.ContactList.getContactList(list.id)
                    .then(response => {
                        const listData = response.data;
                        if (listData.no_of_contacts > this.minimumContactsToSplit) {
                            this.selected_model = cloneDeep(listData);
                            if (this.$refs.split_list_form) {
                                this.$refs.split_list_form.show_dialog = true;
                            }
                        } else {
                            this.$notify({
                                offset: 75,
                                title: 'Contact List Split Error',
                                message: `The list must have more than ${this.minimumContactsToSplit} contacts to be split.`,
                                type: 'error',
                                showClose: true,
                                duration: 5000
                            });
                        }
                    })
                    .catch(err => {
                        console.error("Error fetching contact list details:", err);
                        this.$root.handleErrors(err.response);
                    })
                    .finally(() => {
                        this.isCheckingListCount = false;
                    });
            },

            closeAssignmentForm() {
                this.selected_model = {}
            },

            closeSplitListForm() {
                this.selected_model = {}
            },

            hideSplitListForm() {
                this.selected_model = {}
            },

            importContact(from) {
                if (from === 'import_from_csv') {
                    if (this.$refs.csv_import) {
                        this.$refs.csv_import.start()
                    }
                }
                else if (from === 'import_from_hubspot') {
                    if (this.$refs.import_from_hubspot_list) {
                        this.$refs.import_from_hubspot_list.show_dialog = true
                    }
                }
                else if (from === 'import_from_salesforce') {
                    if (this.$refs.import_from_salesforce_list) {
                        this.$refs.import_from_salesforce_list.show_dialog = true
                    }
                }
            },

            searchContactLists: debounce(function () {
                this.fetchContactList()
            }, 300),

            fetchContactList(page) {
                this.loading = true
                this.filters.page = page

                this.$APIV2.ContactList.getContactsList(this.filters)
                    .then(res => {
                        this.pagination = res.data
                    }).catch(err => {
                        console.log(err)
                    }).finally(_ => {
                        this.loading = false
                    })
            },

            handleShowInPublicFolder(contact_list) {
                let show = !contact_list.show_in_public_folder

                this.$confirm(`Are you sure you want to ${show ? 'show' : 'remove'} this in public folder?`, 'Confirm', {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    customClass: 'width-500 fixed',
                    confirmButtonClass: 'el-button--success',
                    type: 'warning'
                }).then(() => {
                    let post_data = {
                        show_in_public_folder: show,
                        from_admin_list: true
                    }

                    this.updateContactList(post_data, contact_list)
                }).catch(() => {
                    //
                });
            },

            stageToEditContactList(contact_list) {
                this.show_dialog = true
                this.selected_model = cloneDeep(contact_list)
            },

            resetProperties() {
                this.selected_model = {}
                this.show_dialog = false

                this.errors.clearAll()
            },

            updateContactListName() {
                if (this.selected_model) {
                    this.$refs.rename_form.validate(valid => {
                        if (valid) {
                            let post_data = {
                                name: this.selected_model.name,
                                from_admin_list: true
                            }

                            this.updateContactList(post_data, this.selected_model)
                        }
                    })
                }
            },

            updateContactList(post_data, contact_list) {
                this.$APIV2.ContactList.updateContactList(post_data, contact_list.id)
                    .then(res => {
                        let index = this.pagination.data.findIndex(cl => cl.id === contact_list.id)

                        if (index > -1) {
                            res.data.data.no_of_contacts = contact_list.no_of_contacts

                            this.pagination.data.splice(index, 1, res.data.data)

                            this.notification('success', `Contact list <b>${res.data.data.name}</b> has been successfully updated`)

                            this.resetProperties()
                        }
                    })
                    .catch(err => {
                        if (err.response.status === 422) {
                            this.errors = new ErrorBag(err.response.data.errors)
                        }
                    })
            },

            deleteContactList(contact_list) {
                this.$confirm(`Are you sure you want to delete this contact list? This action is irreversible.`, 'Warning', {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    customClass: 'w-50 fixed',
                    confirmButtonClass: 'el-button--danger',
                    type: 'warning'
                }).then(() => {
                    let params = {
                        from_admin_list: true
                    }

                    this.$APIV2.ContactList.deleteContactList(contact_list.id, params)
                    .then(res => {
                        let index = this.pagination.data.findIndex(cl => cl.id === contact_list.id)

                        if (index > -1) {
                            this.pagination.data.splice(index, 1)

                            this.pagination.total--

                            this.notification('success', `Contact list <b>${contact_list.name}</b> has been deleted.`)
                        }
                    })
                }).catch(() => {});
            },

            closeDialog() {
                if (this.isNameChanged) {
                    this.$confirm(`Are you sure you want to close this form?`, 'Contact List', {
                        confirmButtonText: "Yes, I'm sure",
                        cancelButtonText: "No, I'm not",
                        type: 'warning',
                        customClass: 'width-500 fixed'
                    }).then(() => {
                        this.resetProperties()
                    }).catch(err => {})

                    return
                }

                this.resetProperties()
            },

            notification(type, message) {
                this.$notify({
                    offset: 75,
                    title: 'Contact List',
                    message: message,
                    type: type,
                    dangerouslyUseHTMLString: true,
                    showClose: true,
                    duration: 5000
                })
            },

            handleImportStarted() {
                this.fetchContactList(1)
            },

            shouldShowSplitOption(list) {
                const isAgent = auth.user.profile.role_names.includes('Company Agent')
                if (list.show_in_public_folder && isAgent) {
                    return false
                }
                return list.type === ContactListTypes.STATIC_LIST
            },

            addToPowerDialer(list) {
                if (this.$refs.add_to_power_dialer) {
                    this.$refs.add_to_power_dialer.show_dialog = true
                    this.$refs.add_to_power_dialer.contact_list = list
                }
            },


            onSortChange({ prop: order_by, order }) {
                this.filters.sort = 'created_at'

                if (order) {
                    const sortColumnMap = {
                        'import_status_name': 'import_status',
                        'owner_name': 'owner_name',
                        'source_name': 'source'
                    }

                    order_by = sortColumnMap[order_by] ?? `contact_lists.${order_by}`

                    this.filters.sort = order_by
                }

                this.filters.order = order === 'ascending' ? 'asc' : 'desc'
                this.fetchContactList(1)
            },
        },

        beforeRouteEnter(to, from, next) {
            auth.check()
                .then((res) => {
                    let auth = {
                        user: {
                            profile: res.data.user
                        }
                    }

                    if (res.data.user.is_reseller) {
                        // redirect to account management portal if the company is a reseller
                        next({name: 'Account Management Portal'})
                    } else {
                        next(vm => {
                            if (!vm.hasRole('Company Admin') && !vm.hasRole('Company Supervisor')) {
                                next({name: 'Dashboard'})
                            }
                        })
                    }
                })
                .catch((err) => {
                    next({name: 'Login', query: {redirect: to.fullPath}})
                })
        },

        beforeDestroy() {
            VueEvent.unlisten('contact_list_import_csv')
        }
    }
</script>
<style scoped>
.salesforce-icon-container {
    color: #0C9ED9;
}
</style>
